import * as React from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';

interface InputFileDisplayProps {
    fileName: string;
    fileSize: string;
    onDelete: () => void;
}

const StyledBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 28px',
    background: theme.palette.transparentPurple.main,
    borderRadius: '8px',
    border: `1px solid ${theme.palette.lightGrey.main}`,
    color: theme.palette.secondary.main,
}));

const FileInfo = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
}));

const FileDetails = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
}));

const FileName = styled(Typography)(({ theme }) => ({
    fontSize: '14px',
    fontWeight: 400,
    color: theme.palette.secondary.main,
}));

const FileSize = styled(Typography)(({ theme }) => ({
    fontSize: '14px',
    fontWeight: 400,
    color: theme.palette.border.main,
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.primary.contrastText,
}));

const UploadContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 28px',
    background: theme.palette.transparentPurple.main,
    borderRadius: '8px',
    border: `1px dashed ${theme.palette.lightGrey.main}`,
    cursor: 'pointer',
    color: theme.palette.secondary.main,

    '& .upload': {
        minWidth: '165px',
        color: theme.palette.primary.main,
        background: theme.palette.secondary.main,
        fontSize: '12px',
        fontWeight: 700,
        lineHeight: '24px',
        borderRadius: '24px',
    },
}));

const HiddenInput = styled('input')({
    display: 'none',
});

const InputFileDisplay: React.FC<InputFileDisplayProps> = ({ fileName, fileSize, onDelete }) => {
    return (
        <StyledBox>
            <FileInfo>
                <i className="fi fi-br-document" style={{ fontSize: 28 }} />
                <FileDetails>
                    <FileName>{fileName}</FileName>
                    <FileSize>{`(${fileSize})`}</FileSize>
                </FileDetails>
            </FileInfo>
            <StyledIconButton onClick={onDelete}>
                <i className="fi fi-br-trash" style={{ fontSize: 24 }} />
            </StyledIconButton>
        </StyledBox>
    );
};

interface InputFileUploaderProps {
    label: string;
    maxSize: string;
}

const InputFileUploader: React.FC<InputFileUploaderProps> = ({ label, maxSize }) => {
    const [file, setFile] = React.useState<File | null>(null);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];
        if (selectedFile) {
            setFile(selectedFile);
        }
    };

    const handleDelete = () => {
        setFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const formatFileSize = (size: number): string => {
        if (size < 1024) return `${size} B`;
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
        return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
        <Box>
            <HiddenInput type="file" ref={fileInputRef} onChange={handleFileChange} />
            {file ? (
                <InputFileDisplay fileName={file.name} fileSize={formatFileSize(file.size)} onDelete={handleDelete} />
            ) : (
                <UploadContainer onClick={handleUploadClick}>
                    <FileInfo>
                        <i className="fi fi-br-document" style={{ fontSize: 28 }} />
                        <FileDetails>
                            <FileName>
                                {label}
                                <Box component="span" sx={{ ml: '4px', color: '#C0C0C0' }}>{`(${maxSize})`}</Box>
                            </FileName>
                        </FileDetails>
                    </FileInfo>
                    <Button className="upload">Parcourir les fichiers</Button>
                </UploadContainer>
            )}
        </Box>
    );
};

export default InputFileUploader;
